"use client";
import { Colors } from "@/enums/Colors";
import AuthService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/AuthService";
import { LazoChatConversationShell } from "@/models/ai-chat/LazoChatConversation";
import { SmallConversationMember } from "@/models/ai-chat/SmallConversationMember";
import LazoChatMessageService from "@/services/ai-chat/LazoChatMessageService";
import { ConversationType } from "@/types/ConversationType";
import { getValidFiles } from "@/utils/LazoFIleUtils";
import { AttachFileOutlined } from "@mui/icons-material";
import { Box, SxProps, Typography, styled } from "@mui/material";
import Button from "@mui/material/Button";
import dynamic from "next/dynamic";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MentionItem, SuggestionDataItem } from "react-mentions";
import { DEFAULT_FORMATS, DEFAULT_TOOLBAR_OPTIONS } from "../utils/Constants";
import LazoTooltip from "../utils/LazoTooltip";
import PaperPlaneOutlined from "./../../../assets/images/paper-plane-send-icon.svg";
import { SendMessageFn } from "./ChatContainer";
import FilesContainer from "./FilesContainer";
const QuillEditor = dynamic(() => import("@/components/task-components/QuillEditor"), { ssr: false });

interface ChatTextFieldProps {
  onSendMessage: SendMessageFn;
  selectedChat: LazoChatConversationShell;
  members: SmallConversationMember[];
}

const ChatTextFieldComponent = ({ onSendMessage, selectedChat, members }: ChatTextFieldProps) => {
  const { t } = useTranslation("chat");
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [textboxContent, setTextboxContent] = useState<{ formatted: string; raw: string }>({ formatted: "", raw: "" });
  const [mentions, setMentions] = useState<MentionItem[]>([]);
  const [suggestionsArray, setSuggestionsArray] = useState<SuggestionDataItem[]>([]);

  const [lastTypingTime, setLastTypingTime] = useState<number>(0);

  const handleChange = (event: { target: { name: string; value: string } }, text: string, mentions?: MentionItem[]) => {
    setTextboxContent({
      formatted: event.target.value,
      raw: text,
    });

    setMentions(mentions || []);

    if (selectedChat?.externalId && new Date().getTime() - lastTypingTime >= 3000) {
      LazoChatMessageService.sendTypingEvent(selectedChat?.externalId as string, event.target.value.trim() !== "");
      setLastTypingTime(new Date().getTime());
    }
  };

  function handleSendMessage() {
    const isEmpty = !textboxContent.raw || textboxContent.raw.trim() === "";
    if (isEmpty && files.length <= 0) return;
    onSendMessage(!isEmpty ? textboxContent.formatted : null, selectedChat, mentions, files);
    setTextboxContent({ formatted: "", raw: "" });
    setFiles([]);
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = getValidFiles(Array.from(e.target.files));
      setFiles([...files, ...newFiles]);
      e.target.value = "";
    }
  };

  const handleAttachFile = () => {
    fileInputRef.current?.click();
  };

  const currentUserExternalId = AuthService.getUser()?.externalId;

  const isOneOnOneWithInactiveUser =
    selectedChat?.conversationType === ConversationType.PRIVATE &&
    members?.some((m) => m.userExternalId !== currentUserExternalId && !m.isAvailable);

  useEffect(() => {
    setSuggestionsArray(mapToSuggestions());
  }, [selectedChat?.externalId]);

  function mapToSuggestions() {
    const tempArray = [];
    if (members) {
      members.forEach((member) => {
        if (member.userExternalId !== currentUserExternalId) {
          tempArray.push({
            id: member.userExternalId!,
            display: member.firstName + " " + member.lastName,
          });
        }
      });
    }
    return tempArray;
  }

  function onFilePaste(f: File): void {
    setFiles((prev) => [...prev, f]);
  }

  if (isOneOnOneWithInactiveUser) {
    return (
      <Box display="flex" flexDirection="column" width="100%" gap="0.25rem">
        <InactiveUserContainer>
          <Typography sx={InactiveUserMessageSx}>{t("textField.inactiveUserMessage")}</Typography>
        </InactiveUserContainer>
        <Typography sx={ExplanationTextSx}>{t("textField.inboxDisclaimer")}</Typography>
      </Box>
    );
  }

  return (
    <>
      <Box display="flex" flexDirection="column" width="100%" gap="0.25rem">
        <ChatTextFieldContainer hasFiles={files.length !== 0}>
          <QuillContainer translate="no">
            <QuillEditor
              value={textboxContent.formatted}
              onChange={handleChange}
              onFilePaste={onFilePaste}
              options={{
                formats: [...DEFAULT_FORMATS, "mention"],
                modules: {
                  toolbar: DEFAULT_TOOLBAR_OPTIONS,
                },
                placeholder: t("textField.placeholder"),
                theme: "snow",
              }}
            />
          </QuillContainer>

          <ButtonsContainer hasFiles={files.length !== 0}>
            <LazoTooltip title={t("textField.fileUploadTooltip")}>
              <div onClick={handleAttachFile}>
                <input ref={fileInputRef} id="file" type="file" multiple style={{ display: "none" }} onChange={handleFileChange} />
                <AttachFileOutlined
                  sx={{
                    transform: "scale(1.4) rotate(40deg)",
                    ":hover": { cursor: "pointer" },
                  }}
                />
              </div>
            </LazoTooltip>
            <FilesContainer files={files} setFiles={setFiles} textSx={{ maxWidth: "5rem" }} />
            <ChatButton variant="text" color="primary" onClick={handleSendMessage}>
              <img style={{ width: "16px", height: "16px" }} alt={"paper-plane-img"} src={PaperPlaneOutlined.src} />
            </ChatButton>
          </ButtonsContainer>
        </ChatTextFieldContainer>
        <Typography sx={ExplanationTextSx}>{t("textField.inboxDisclaimer")}</Typography>
      </Box>
    </>
  );
};

const ChatTextFieldContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "hasFiles",
})<{ hasFiles: boolean }>(({ hasFiles }) => ({
  borderRadius: "1rem",
  minHeight: hasFiles ? "12rem" : "8.875rem",
  height: "auto",
  position: "relative",
  border: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  zIndex: 0,
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  backgroundColor: Colors.WHITE,
  padding: "0.25rem 0.75rem",
}));

const InactiveUserContainer = styled(Box)(() => ({
  borderRadius: "1rem",
  minHeight: "4.5rem",
  border: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  backgroundColor: Colors.SURFACE_MUTED,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "1rem",
}));

const InactiveUserMessageSx: SxProps = {
  fontSize: "16px",
  color: Colors.DARKER_DARKER_GRAY,
  fontStyle: "italic",
  textAlign: "center",
};

const ButtonsContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "hasFiles",
})<{ hasFiles: boolean }>(({ hasFiles }) => ({
  position: "sticky",
  bottom: 0,
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "end",
  paddingRight: "0.5rem",
  height: hasFiles ? "4rem" : "1.5rem",
  gap: "0.3rem",
}));

const ChatButton = styled(Button)(({}) => ({
  padding: 0,
  margin: ".2rem",
  borderRadius: "50%",
  height: "1rem",
  minWidth: "1rem",
}));

const ExplanationTextSx: SxProps = {
  fontSize: "12px",
  color: Colors.DARKER_DARKER_GRAY,
  display: "flex",
  justifyContent: "center",
  width: "100%",
  textAlign: "center",
};

const QuillContainer = styled("div")(() => ({
  ".ql-toolbar.ql-snow": {
    border: 0,
    borderBottom: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  },
  ".ql-container.ql-snow": {
    border: 0,
  },
}));

export default ChatTextFieldComponent;
